import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import { Button } from '@openx/components-core/lib/Button/Button';
import { Checkbox } from '@openx/components-core/lib/Checkbox/Checkbox';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type LookalikeSegmentDefinition,
  type SimilarityLevelValue,
  similarityLevels,
} from '../../../../../../../types';
import type { LookalikeDefinitionSegments } from '../../../hooks/lookalike/lookalikeSegmentsReducer';

type LookalikeSelectionModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (selection: { similarityLevel: SimilarityLevelValue; includeSeedSegment: boolean }) => void;
  definition: LookalikeSegmentDefinition;
  isEditMode?: boolean;
  existingSegments?: LookalikeDefinitionSegments;
};

const SIMILARITY_LEVELS = [similarityLevels.High, similarityLevels.Balanced, similarityLevels.Scaled] as const;

export const LookalikeSelectionModal = ({
  open,
  onClose,
  onConfirm,
  definition,
  isEditMode = false,
  existingSegments,
}: LookalikeSelectionModalProps) => {
  const { t } = useTranslation();

  const initialSelectedSimilarityLevel = useMemo(() => {
    if (isEditMode && existingSegments?.similaritySegment?.propensity) {
      return existingSegments.similaritySegment.propensity as SimilarityLevelValue;
    }
  }, [isEditMode, existingSegments]);

  const initialIncludeSeedSegment = useMemo(() => {
    return !!(isEditMode && existingSegments?.seedSegment);
  }, [isEditMode, existingSegments]);

  const [selectedSimilarityLevel, setSelectedSimilarityLevel] = useState<SimilarityLevelValue | undefined>(
    initialSelectedSimilarityLevel
  );

  const [includeSeedSegment, setIncludeSeedSegment] = useState<boolean>(initialIncludeSeedSegment);

  const availableSimilarityLevels = useMemo(
    () =>
      definition.lookalike_segment_similarities
        .map(sim => sim.similarity_level as SimilarityLevelValue)
        .filter((level: SimilarityLevelValue) => SIMILARITY_LEVELS.includes(level))
        .map(level => ({ id: level, name: level })),
    [definition]
  );

  const similarityLevelValue = useMemo(
    () => availableSimilarityLevels.find(level => level.id === selectedSimilarityLevel) || null,
    [availableSimilarityLevels, selectedSimilarityLevel]
  );

  const hasSeedSegment = !!definition.lookalike_segment_seed_aggregate;

  const handleConfirm = useCallback(() => {
    if (selectedSimilarityLevel) {
      onConfirm({
        similarityLevel: selectedSimilarityLevel,
        includeSeedSegment: includeSeedSegment && hasSeedSegment,
      });
      onClose();
    }
  }, [selectedSimilarityLevel, includeSeedSegment, hasSeedSegment, onConfirm, onClose]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      aria-labelledby="lookalike-selection-dialog-title"
    >
      <DialogTitle
        id="lookalike-selection-dialog-title"
        data-test="edit-similarity-reach-dialog-title"
        sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {isEditMode ? t('Edit Similarity Reach') : t('Select Similarity Reach')}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <AutoComplete
            data-test="similarity-level-select"
            textFieldProps={{ label: t('Similarity'), placeholder: t('Select Similarity Level') }}
            options={availableSimilarityLevels}
            value={similarityLevelValue}
            onChange={(_, value) => {
              setSelectedSimilarityLevel(value?.id);
            }}
            getOptionLabel={option => option.name}
            fullWidth
          />

          {hasSeedSegment && (
            <FormControlLabel
              control={
                <Checkbox
                  data-test="include-seed-segment-checkbox"
                  checked={includeSeedSegment}
                  disabled={!selectedSimilarityLevel}
                  onChange={(_, checked) => setIncludeSeedSegment(checked)}
                />
              }
              label={t('Include Seed Segment')}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={handleCancel} data-test="cancel-lookalike-selection">
          {t('CANCEL')}
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          data-test="confirm-lookalike-selection"
          disabled={!selectedSimilarityLevel}
        >
          {t('APPLY')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
